import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Copy, Check, ShieldCheck, ShieldAlert, User as UserIcon,
  Package, Wallet, Ban, KeyRound, Trash2, ExternalLink, Mail, Phone,
  MapPin, Calendar,
} from 'lucide-react';
import UserAvatar from '@/components/marketplace/UserAvatar';
import { useAuth } from '@/lib/auth';

const StatusPill = ({ status }) => {
  const map = {
    active: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700',
    suspended: 'bg-red-500/10 border-red-500/30 text-red-700',
    banned: 'bg-red-500/10 border-red-500/30 text-red-700',
    pending: 'bg-amber-500/10 border-amber-500/30 text-amber-700',
  };
  return (
    <Badge variant='outline' className={`text-[10px] ${map[status] || 'bg-secondary text-muted-foreground'}`}>
      {status || 'active'}
    </Badge>
  );
};

const InfoRow = ({ label, value, empty = '—', testid }) => (
  <div className='py-2 border-b last:border-b-0'>
    <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>{label}</div>
    <div className='text-sm mt-0.5' data-testid={testid}>{value ? value : <span className='text-muted-foreground italic'>{empty}</span>}</div>
  </div>
);

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUuid, setShowUuid] = useState(false);
  const [copied, setCopied] = useState(false);
  const [gstBusy, setGstBusy] = useState(false);
  const [rejReason, setRejReason] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  // Controlled tab state so that a background reload() (triggered by add/
  // delete note) doesn't unmount+remount Tabs and snap the user back to
  // Overview. Fixes the state-reset bug found by testing agent it_38.
  const [activeTab, setActiveTab] = useState('overview');
  // Admin billing tab — lazily loaded when the user opens it.
  const [billing, setBilling] = useState({ loaded: false, invoices: [], transactions: [] });

  useEffect(() => {
    if (activeTab !== 'invoices' || billing.loaded) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/invoices/user/${userId}`);
        setBilling({ loaded: true, invoices: d.invoices || [], transactions: d.transactions || [] });
      } catch { setBilling(b => ({ ...b, loaded: true })); }
    })();
  }, [activeTab, userId, billing.loaded]);

  const downloadUserInvoice = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download invoice'); }
  };
  // Admin-only note deletion — moderators can add but never delete so the
  // audit trail is preserved for future staff.
  const { user: currentAdmin } = useAuth();
  const canDeleteNotes = currentAdmin?.role === 'admin';

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setNoteBusy(true);
    try {
      await api.post(`/admin/users/${userId}/notes`, { text });
      setNoteDraft('');
      toast.success('Note added');
      await refresh();
    } catch (e) { toast.error(extractApiError(e, 'Failed to add note')); }
    finally { setNoteBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/users/${userId}/notes/${noteId}`);
      toast.success('Note deleted');
      await refresh();
    } catch (e) { toast.error(extractApiError(e, 'Failed — only admins can delete notes')); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [detailRes, listingsRes] = await Promise.all([
        api.get(`/admin/users/${userId}/detail`),
        api.get(`/admin/users/${userId}/listings`, { params: { limit: 100 } }),
      ]);
      setData(detailRes.data);
      setListings(listingsRes.data.items || []);
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to load user'));
      navigate('/admin');
    } finally { setLoading(false); }
  };

  // Silent refresh used by inline actions (add/delete note, GST decisions
  // that already show their own toast) — DOES NOT toggle loading so the
  // Tabs component (and the user's current tab) stay mounted. Without this
  // the Notes tab would snap back to Overview after every note action.
  const refresh = async () => {
    try {
      const detailRes = await api.get(`/admin/users/${userId}/detail`);
      setData(detailRes.data);
    } catch {
      /* leave existing data in place — user still sees prior state */
    }
  };
  useEffect(() => { load(); }, [userId]);

  const copyUuid = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      toast.success('UUID copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  // GST verification / revocation flows. If a document is pending (has a
  // verifications record with status='pending'), use /verification/:id/review.
  // Otherwise use the direct admin toggle endpoint.
  const gstApprove = async () => {
    setGstBusy(true);
    try {
      const gst = data.gst_verification;
      if (gst && gst.status === 'pending') {
        await api.post(`/verification/${gst.id}/review`, { approve: true, reason: '' });
      } else {
        await api.post(`/verification/gst/${userId}?verified=true`);
      }
      toast.success('GST verified for this user');
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Verification failed')); }
    finally { setGstBusy(false); }
  };

  const gstReject = async () => {
    if (!rejReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setGstBusy(true);
    try {
      const gst = data.gst_verification;
      if (gst && gst.status === 'pending') {
        await api.post(`/verification/${gst.id}/review`, { approve: false, reason: rejReason.trim() });
      } else {
        await api.post(`/verification/gst/${userId}?verified=false`);
      }
      toast.success('GST verification removed');
      setRejReason('');
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Failed')); }
    finally { setGstBusy(false); }
  };

  const gstRevoke = async () => {
    if (!window.confirm('Revoke this user\'s GST verification? They will lose the "GST Verified" badge across the platform.')) return;
    setGstBusy(true);
    try {
      await api.post(`/verification/gst/${userId}?verified=false`);
      toast.success('GST verification revoked');
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Failed')); }
    finally { setGstBusy(false); }
  };

  const banUser = async () => {
    const reason = window.prompt('Reason for ban / suspension (visible in email to user):');
    if (!reason) return;
    try {
      await api.patch(`/admin/users/${userId}`, { status: 'suspended' });
      toast.success('User suspended');
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Failed')); }
  };

  const unbanUser = async () => {
    try {
      await api.patch(`/admin/users/${userId}`, { status: 'active' });
      toast.success('User reinstated');
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Failed')); }
  };

  const sendPasswordReset = async () => {
    try {
      await api.post(`/admin/users/${userId}/password-reset`);
      toast.success('Password reset email sent');
    } catch (e) { toast.error(extractApiError(e, 'Failed')); }
  };

  if (loading || !data) {
    return <div className='indus-container py-12 text-sm text-muted-foreground' data-testid='user360-loading'>Loading user profile…</div>;
  }

  const { user, counts, ledger, payments } = data;
  const gst = data.gst_verification;
  const activeSub = data.active_subscription;
  const adminNotes = data.admin_notes || [];

  // Plan badge: prefer real subscription; fall back to Free.
  const planBadge = activeSub && activeSub.active && activeSub.expires_at
    ? {
        label: activeSub.plan_name || activeSub.plan_id || 'PAID',
        expires: activeSub.expires_at,
        color: 'bg-primary/10 border-primary/30 text-primary',
      }
    : { label: 'FREE', expires: null, color: '' };

  const gstStatusUi = user.is_gst_verified
    ? { label: 'GST Verified', color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700', icon: ShieldCheck }
    : (gst && gst.status === 'pending'
        ? { label: 'GST Pending Review', color: 'bg-blue-500/10 border-blue-500/30 text-blue-700', icon: ShieldAlert }
        : (gst && gst.status === 'rejected'
            ? { label: 'GST Rejected', color: 'bg-red-500/10 border-red-500/30 text-red-700', icon: ShieldAlert }
            : { label: 'GST Not Submitted', color: 'bg-muted text-muted-foreground', icon: ShieldAlert }));
  const GstIcon = gstStatusUi.icon;

  return (
    <div className='indus-container py-8 space-y-6' data-testid='user360-page'>
      {/* Header */}
      <div className='flex items-start gap-4 flex-wrap'>
        <Link to='/admin' className='text-sm text-muted-foreground hover:text-foreground inline-flex items-center' data-testid='user360-back'>
          <ChevronLeft className='h-4 w-4' /> Back to admin
        </Link>
      </div>

      <Card className='p-6'>
        <div className='flex items-start gap-4 flex-wrap'>
          <UserAvatar
            name={user.name}
            photo={user.profile_photo_thumb || user.profile_photo}
            updatedAt={user.profile_photo_updated_at}
            size='lg'
            data-testid='user360-avatar'
          />
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <h1 className='text-2xl font-semibold tracking-tight' data-testid='user360-name'>{user.name || user.email}</h1>
              <StatusPill status={user.status} />
              <Badge variant='outline' className='text-[10px] uppercase'>{user.role}</Badge>
              <Badge
                variant='outline'
                className={`text-[10px] uppercase ${planBadge.color}`}
                data-testid='user360-plan-badge'
                title={planBadge.expires ? `Valid until ${new Date(planBadge.expires).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'No paid subscription'}
              >
                {planBadge.label}
                {planBadge.expires && (
                  <span className='ml-1 normal-case text-[10px] font-normal opacity-90'>
                    · exp {new Date(planBadge.expires).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                  </span>
                )}
              </Badge>
              <Badge variant='outline' className={`text-[10px] ${gstStatusUi.color}`} data-testid='user360-gst-badge'>
                <GstIcon className='h-3 w-3 mr-1' /> {gstStatusUi.label}
              </Badge>
            </div>
            {user.company_name && <div className='text-sm text-muted-foreground mt-1' data-testid='user360-company'>{user.company_name}</div>}
            <div className='flex items-center gap-3 mt-3 text-xs flex-wrap'>
              {!showUuid ? (
                <Button size='sm' variant='outline' onClick={() => setShowUuid(true)} data-testid='user360-show-uuid'>
                  Show UUID
                </Button>
              ) : (
                <div className='flex items-center gap-1 rounded-md border bg-secondary px-2 py-1 font-mono text-[11px]' data-testid='user360-uuid'>
                  {userId}
                  <button onClick={copyUuid} className='hover:text-primary ml-1' aria-label='Copy UUID' data-testid='user360-copy-uuid'>
                    {copied ? <Check className='h-3 w-3 text-emerald-600' /> : <Copy className='h-3 w-3' />}
                  </button>
                </div>
              )}
              <span className='text-muted-foreground inline-flex items-center gap-1'>
                <Calendar className='h-3 w-3' /> Joined {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className='flex gap-2 flex-wrap'>
            <Button variant='outline' size='sm' onClick={sendPasswordReset} data-testid='user360-reset-btn'>
              <KeyRound className='h-3.5 w-3.5 mr-1' /> Send password reset
            </Button>
            {user.status === 'suspended' ? (
              <Button variant='outline' size='sm' onClick={unbanUser} data-testid='user360-unban-btn'>
                Reinstate
              </Button>
            ) : (
              <Button variant='outline' size='sm' className='text-destructive hover:text-destructive' onClick={banUser} data-testid='user360-ban-btn'>
                <Ban className='h-3.5 w-3.5 mr-1' /> Suspend
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Stat cards */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <StatCard icon={Package} label='Total listings' value={counts.total_listings} testid='user360-stat-listings' />
        <StatCard icon={Mail} label='Inquiries received' value={counts.inquiries_received} testid='user360-stat-inq-received' />
        <StatCard icon={Wallet} label='Points balance' value={counts.points_balance} testid='user360-stat-points' />
        <StatCard icon={UserIcon} label='Total spend' value={`₹${(counts.total_spend || 0).toLocaleString('en-IN')}`} testid='user360-stat-spend' />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid='user360-tabs'>
          <TabsTrigger value='overview' data-testid='user360-tab-overview'>Overview</TabsTrigger>
          <TabsTrigger value='listings' data-testid='user360-tab-listings'>Listings ({counts.total_listings})</TabsTrigger>
          <TabsTrigger value='payments' data-testid='user360-tab-payments'>Payments &amp; Rewards</TabsTrigger>
          <TabsTrigger value='invoices' data-testid='user360-tab-invoices'>Invoices &amp; Tx</TabsTrigger>
          <TabsTrigger value='notes' data-testid='user360-tab-notes'>Notes ({adminNotes.length})</TabsTrigger>
        </TabsList>

        {/* ---------- OVERVIEW ---------- */}
        <TabsContent value='overview' className='mt-4 grid md:grid-cols-2 gap-4'>
          <Card className='p-5'>
            <div className='text-sm font-semibold mb-3 flex items-center gap-2'><UserIcon className='h-4 w-4' /> Contact</div>
            <InfoRow label='Email' value={user.email} testid='user360-email' />
            <InfoRow label='Mobile' value={user.mobile} testid='user360-mobile' />
            <InfoRow label='Industrial area' value={user.industrial_area} testid='user360-industrial-area' />
            <InfoRow label='City' value={user.city} testid='user360-city' />
            <InfoRow label='State' value={user.state} testid='user360-state' />
            <InfoRow label='Full address (private)' value={user.full_address} testid='user360-full-address' />
          </Card>

          <Card className='p-5'>
            <div className='text-sm font-semibold mb-3 flex items-center gap-2'><ShieldCheck className='h-4 w-4' /> GST verification</div>
            <div className='space-y-3'>
              <div className={`rounded-md border p-3 ${gstStatusUi.color}`}>
                <div className='flex items-center gap-2 text-sm font-semibold'>
                  <GstIcon className='h-4 w-4' /> {gstStatusUi.label}
                </div>
                {user.gst_number && <div className='text-xs mt-1 font-mono' data-testid='user360-gst-number'>GSTIN: {user.gst_number}</div>}
                {user.gst_verified_at && <div className='text-[11px] mt-0.5 opacity-80'>Verified on {new Date(user.gst_verified_at).toLocaleString('en-IN')}</div>}
              </div>

              {gst && gst.document_url && (
                <a href={gst.document_url} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-xs text-primary hover:underline' data-testid='user360-gst-doc-link'>
                  <ExternalLink className='h-3 w-3' /> View submitted document
                </a>
              )}

              <div className='flex flex-wrap gap-2 pt-2 border-t'>
                {!user.is_gst_verified && (
                  <Button size='sm' onClick={gstApprove} disabled={gstBusy} data-testid='user360-gst-approve-btn'>
                    <ShieldCheck className='h-3.5 w-3.5 mr-1' /> Verify GST
                  </Button>
                )}
                {user.is_gst_verified && (
                  <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={gstRevoke} disabled={gstBusy} data-testid='user360-gst-revoke-btn'>
                    <ShieldAlert className='h-3.5 w-3.5 mr-1' /> Revoke verification
                  </Button>
                )}
                {gst && gst.status === 'pending' && (
                  <div className='w-full'>
                    <Label className='text-xs mt-1'>Or reject with a reason</Label>
                    <div className='flex gap-2'>
                      <Textarea rows={2} value={rejReason} onChange={(e) => setRejReason(e.target.value)} placeholder='Document illegible / mismatch / expired…' data-testid='user360-gst-reject-reason' />
                      <Button size='sm' variant='outline' className='text-destructive hover:text-destructive shrink-0' onClick={gstReject} disabled={gstBusy || !rejReason.trim()} data-testid='user360-gst-reject-btn'>Reject</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className='p-5 md:col-span-2'>
            <div className='text-sm font-semibold mb-3'>Company profile</div>
            <InfoRow label='Company name' value={user.company_name} />
            <InfoRow label='Description' value={user.company_description} />
            <InfoRow label='Verification level' value={<Badge variant='outline'>{user.verification_level || 'unverified'}</Badge>} />
          </Card>
        </TabsContent>

        {/* ---------- LISTINGS ---------- */}
        <TabsContent value='listings' className='mt-4'>
          <Card className='p-4'>
            {listings.length === 0 ? (
              <div className='py-8 text-center text-sm text-muted-foreground' data-testid='user360-no-listings'>This user has no listings yet.</div>
            ) : (
              <table className='w-full text-sm' data-testid='user360-listings-table'>
                <thead className='text-left border-b'>
                  <tr>
                    <th className='py-2 pr-3'>Title</th>
                    <th className='py-2 pr-3'>Type</th>
                    <th className='py-2 pr-3'>Category</th>
                    <th className='py-2 pr-3'>Location</th>
                    <th className='py-2 pr-3'>Views</th>
                    <th className='py-2 pr-3'>Status</th>
                    <th className='py-2 pr-3'>Created</th>
                    <th className='py-2 pr-3'></th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map(l => (
                    <tr key={l.id} className='border-b last:border-b-0 hover:bg-secondary/40'>
                      <td className='py-2 pr-3 max-w-[280px]'><span className='line-clamp-2'>{l.title}</span></td>
                      <td className='py-2 pr-3 text-[10px] uppercase text-muted-foreground'>{l.listing_type}</td>
                      <td className='py-2 pr-3 text-xs'>{l.category_name || '—'}</td>
                      <td className='py-2 pr-3 text-xs text-muted-foreground'>{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                      <td className='py-2 pr-3 tabular-nums'>{l.view_count || 0}</td>
                      <td className='py-2 pr-3'><Badge variant='outline' className='text-[10px]'>{l.status}</Badge></td>
                      <td className='py-2 pr-3 text-xs text-muted-foreground'>{new Date(l.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className='py-2 pr-3'>
                        <Link to={`/listing/${l.id}`} target='_blank' className='text-primary hover:underline text-xs inline-flex items-center gap-1'><ExternalLink className='h-3 w-3' /> View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        {/* ---------- PAYMENTS & REWARDS ---------- */}
        <TabsContent value='payments' className='mt-4 grid md:grid-cols-2 gap-4'>
          <Card className='p-4'>
            <div className='text-sm font-semibold mb-3 flex items-center gap-2'><Wallet className='h-4 w-4' /> Recent payments</div>
            {payments.length === 0 ? (
              <div className='text-sm text-muted-foreground py-4' data-testid='user360-no-payments'>No transactions yet.</div>
            ) : (
              <div className='divide-y' data-testid='user360-payments-list'>
                {payments.map(p => (
                  <div key={p.id || p.order_id} className='py-2'>
                    <div className='flex items-center justify-between text-sm'>
                      <div className='font-medium'>{p.description || p.plan_name || 'Payment'}</div>
                      <div className='tabular-nums font-semibold'>₹{(p.amount || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className='flex items-center justify-between text-xs text-muted-foreground mt-0.5'>
                      <span>{p.created_at ? new Date(p.created_at).toLocaleString('en-IN') : ''}</span>
                      <Badge variant='outline' className='text-[10px]'>{p.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className='p-4'>
            <div className='text-sm font-semibold mb-3 flex items-center gap-2'><Wallet className='h-4 w-4' /> Points ledger — recent 20</div>
            {ledger.length === 0 ? (
              <div className='text-sm text-muted-foreground py-4' data-testid='user360-no-ledger'>No points activity yet.</div>
            ) : (
              <div className='divide-y' data-testid='user360-ledger-list'>
                {ledger.map((entry, idx) => (
                  <div key={idx} className='py-2 text-sm'>
                    <div className='flex items-center justify-between'>
                      <div className='text-xs text-muted-foreground uppercase tracking-wider'>{(entry.event_key || entry.source || 'manual').replace(/_/g, ' ')}</div>
                      <div className={`font-semibold tabular-nums ${entry.delta > 0 ? 'text-emerald-700' : 'text-red-700'}`}>{entry.delta > 0 ? '+' : ''}{entry.delta}</div>
                    </div>
                    {(entry.meta?.note || entry.reason) && <div className='text-xs mt-0.5 text-muted-foreground'>{entry.meta?.note || entry.reason}</div>}
                    <div className='text-[11px] text-muted-foreground mt-0.5'>{entry.ts ? new Date(entry.ts).toLocaleString('en-IN') : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------- INVOICES & TRANSACTIONS (admin view) ---------- */}
        <TabsContent value='invoices' className='mt-4 space-y-4'>
          {!billing.loaded ? (
            <Card className='p-10 text-center text-muted-foreground'>Loading billing history…</Card>
          ) : (
            <>
              <Card className='p-4'>
                <div className='text-sm font-semibold mb-3 flex items-center gap-2'><Wallet className='h-4 w-4' /> Invoices ({billing.invoices.length})</div>
                {billing.invoices.length === 0 ? (
                  <div className='text-sm text-muted-foreground py-4'>No invoices for this user.</div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm' data-testid='user360-invoices-table'>
                      <thead className='text-left text-xs uppercase text-muted-foreground'>
                        <tr>
                          <th className='py-2 pr-3'>Invoice #</th>
                          <th className='py-2 pr-3'>Date</th>
                          <th className='py-2 pr-3'>Plan</th>
                          <th className='py-2 pr-3 text-right'>Taxable</th>
                          <th className='py-2 pr-3 text-right'>CGST</th>
                          <th className='py-2 pr-3 text-right'>SGST</th>
                          <th className='py-2 pr-3 text-right'>IGST</th>
                          <th className='py-2 pr-3 text-right'>Total</th>
                          <th className='py-2 pr-3'>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {billing.invoices.map(inv => {
                          const t = inv.tax_snapshot || {};
                          return (
                            <tr key={inv.id} className='border-t' data-testid={`user360-invoice-row-${inv.id}`}>
                              <td className='py-2 pr-3 font-mono text-xs'>{inv.invoice_number}</td>
                              <td className='py-2 pr-3'>{(inv.invoice_date || '').slice(0, 10)}</td>
                              <td className='py-2 pr-3'>{inv.plan_snapshot?.name || '—'}</td>
                              <td className='py-2 pr-3 text-right tabular-nums'>₹ {Number(t.taxable_amount || 0).toFixed(2)}</td>
                              <td className='py-2 pr-3 text-right tabular-nums'>₹ {Number(t.cgst_amount || 0).toFixed(2)}</td>
                              <td className='py-2 pr-3 text-right tabular-nums'>₹ {Number(t.sgst_amount || 0).toFixed(2)}</td>
                              <td className='py-2 pr-3 text-right tabular-nums'>₹ {Number(t.igst_amount || 0).toFixed(2)}</td>
                              <td className='py-2 pr-3 text-right tabular-nums font-semibold'>₹ {Number(t.grand_total || 0).toFixed(2)}</td>
                              <td className='py-2 pr-3'>
                                <Badge variant='outline' className='text-[10px]'>{inv.status}</Badge>
                              </td>
                              <td className='py-2 pr-3'>
                                <Button size='sm' variant='outline' onClick={() => downloadUserInvoice(inv)} data-testid={`user360-invoice-dl-${inv.id}`}>
                                  PDF
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card className='p-4'>
                <div className='text-sm font-semibold mb-3 flex items-center gap-2'><Wallet className='h-4 w-4' /> All transactions ({billing.transactions.length})</div>
                {billing.transactions.length === 0 ? (
                  <div className='text-sm text-muted-foreground py-4'>No transactions.</div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm' data-testid='user360-tx-table'>
                      <thead className='text-left text-xs uppercase text-muted-foreground'>
                        <tr>
                          <th className='py-2 pr-3'>Date</th>
                          <th className='py-2 pr-3'>Plan</th>
                          <th className='py-2 pr-3'>Method</th>
                          <th className='py-2 pr-3'>Amount</th>
                          <th className='py-2 pr-3'>Payment ref / UTR</th>
                          <th className='py-2 pr-3'>Status</th>
                          <th className='py-2 pr-3'>Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billing.transactions.map(t => (
                          <tr key={t.id} className='border-t'>
                            <td className='py-2 pr-3 text-muted-foreground'>{t.created_at ? new Date(t.created_at).toLocaleString('en-IN') : ''}</td>
                            <td className='py-2 pr-3'>{t.plan_name}</td>
                            <td className='py-2 pr-3'>{t.method}</td>
                            <td className='py-2 pr-3 tabular-nums'>₹ {Number(t.amount || 0).toLocaleString('en-IN')}</td>
                            <td className='py-2 pr-3 font-mono text-xs'>{t.razorpay_payment_id || t.manual_utr || t.manual_reference || '—'}</td>
                            <td className='py-2 pr-3'>
                              <Badge variant='outline' className='text-[10px]'>{t.status}</Badge>
                              {t.reject_reason && <div className='text-[10px] text-red-600 mt-0.5'>{t.reject_reason}</div>}
                            </td>
                            <td className='py-2 pr-3 font-mono text-xs'>{t.invoice_number || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* ---------- NOTES (admin/staff-only internal notes) ---------- */}
        <TabsContent value='notes' className='mt-4'>
          <Card className='p-5' data-testid='user360-notes-card'>
            <div className='text-sm font-semibold mb-1'>Internal notes</div>
            <div className='text-xs text-muted-foreground mb-4'>
              Private, admin-only. The user never sees these. Great for logging support calls, past issues, verification back-and-forth, or anything future staff should know.
            </div>
            <div className='flex flex-col sm:flex-row gap-2 mb-4'>
              <Textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={2}
                placeholder='e.g. Called on 12 Feb about GST document rejection. Reissued with correct PDF, verified by mod team.'
                data-testid='user360-note-input'
                maxLength={2000}
              />
              <Button
                onClick={addNote}
                disabled={noteBusy || !noteDraft.trim()}
                data-testid='user360-note-add-btn'
                className='shrink-0 self-end'
              >
                {noteBusy ? 'Saving…' : 'Add note'}
              </Button>
            </div>
            {adminNotes.length === 0 ? (
              <div className='text-sm text-muted-foreground py-4 text-center border-t' data-testid='user360-no-notes'>
                No notes yet.
              </div>
            ) : (
              <div className='space-y-3 border-t pt-3' data-testid='user360-notes-list'>
                {adminNotes.map((n) => (
                  <div key={n.id} className='rounded-md border bg-secondary/30 p-3 text-sm' data-testid={`user360-note-${n.id}`}>
                    <div className='whitespace-pre-wrap break-words'>{n.text}</div>
                    <div className='mt-2 flex items-center justify-between text-[11px] text-muted-foreground'>
                      <span>{n.by_name || 'staff'} · {n.ts ? new Date(n.ts).toLocaleString('en-IN') : ''}</span>
                      {canDeleteNotes && (
                        <button
                          type='button'
                          onClick={() => deleteNote(n.id)}
                          className='text-destructive hover:underline'
                          data-testid={`user360-note-delete-${n.id}`}
                          title='Delete note (admins only)'
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <Card className='p-4' data-testid={testid}>
    <div className='flex items-center justify-between'>
      <div>
        <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>{label}</div>
        <div className='text-xl font-semibold tabular-nums mt-1'>{value ?? 0}</div>
      </div>
      <Icon className='h-4 w-4 text-primary' />
    </div>
  </Card>
);

export default AdminUserDetail;
